export const socialPosts = {
  preview: {
    connectionLevel: '3rd+',
    timeAgo: 'Now',
    seeMore: 'see more',
    seeLess: 'see less',
    commentCount: '{{value}} comments',
    repostCount: '{{value}} reposts',
    author: 'Author',
    genericPosition: 'Your headline on LinkedIn',
    imageAlt: 'Attachment {{index}} of {{total}}',
    companyMentionTitle: 'Company: {{name}}',
    clickToEdit: 'Click to edit',
    editorPlaceholder: 'What do you want to talk about?',
    commentPlaceholder: 'Write the first comment…',
    dismissLink: 'Remove link preview',
    editMode: 'Edit mode',
    previewMode: 'Preview mode',
    desktopView: 'Desktop view',
    mobileView: 'Mobile view',
    characterCount: '{{current}}/{{limit}} characters',
    characterLimitReached: 'Character limit reached',
    characterLimitWarning: '{{remaining}} characters left',

    actions: {
      like: 'Like',
      comment: 'Comment',
      share: 'Repost',
      send: 'Send',
    },

    commentActions: {
      like: 'Like',
      reply: 'Reply',
    },
  },

  mentions: {
    companyBadge: 'Company',
    searching: 'Looking up on LinkedIn…',
  },

  formatting: {
    groupLabel: 'Text formatting',
    bold: 'Bold',
    italic: 'Italic',
    underline: 'Underline',
    strikethrough: 'Strikethrough',
    emoji: 'Emoji',
    searchEmoji: 'Search emoji',
  },

  composer: {
    authorFallback: 'Your LinkedIn profile',

    actions: {
      publish: 'Publish on LinkedIn',
      saveDraft: 'Save draft',
      registerManually: 'Already published it elsewhere? Record it by hand',
      retryLoad: 'Try again',
    },

    account: {
      notConnected:
        'Connect your LinkedIn account to publish from here. You can keep writing and save the draft in the meantime.',
      needsRepair:
        'Your LinkedIn account is not available right now, so publishing is off. You can keep writing and save the draft.',
      goToIntegrations: 'Go to integrations',
    },

    attachments: {
      syncing: 'Saving the images…',
      syncFailed: 'The images could not be saved. Reloading what is stored.',
      unreadable: 'These attachments could not be loaded from storage: {{files}}',
    },

    mentions: {
      searchFailed: 'Could not reach LinkedIn to look up mentions. Keep writing and try again in a moment.',
    },

    published: {
      title: 'This piece is already published on LinkedIn.',
      viewOnLinkedIn: 'View the post',
      firstCommentFailed: 'The post went out, but its first comment did not: {{reason}}',
    },

    lastFailure: {
      title: 'The last attempt to publish this failed (attempt {{attempts}}):',
      caution:
        'If it timed out, the post may have gone out anyway — check on LinkedIn before trying again.',
    },

    stuck: {
      title: 'This publication was left mid-flight.',
      body:
        'We never got an answer from LinkedIn, so this draft cannot be sent again from here. Verify against LinkedIn to settle it automatically — or check by hand and record it.',
      verify: 'Verify against LinkedIn',
      verifying: 'Checking LinkedIn…',
      verifyFailed: 'The verification against LinkedIn could not run.',
      resolvedPublished: 'The post is on LinkedIn — recorded as published.',
      resolvedFailed: 'Verified: the post never went out. You can edit it and try again.',
      inconclusiveTitle: 'The verification could not decide.',
      inconclusiveReasons: {
        mentions_rewrite_text:
          'The post carries mentions, and LinkedIn rewrites them — the returned text can never match ours, so not finding it proves nothing. Check the profile and resolve it by hand.',
        unparseable_created_at:
          'A post with this text was found, but its date could not be read, so we cannot tell whether it is this one. Check the profile and resolve it by hand.',
        page_window_not_covered:
          'LinkedIn returned too many recent posts to see back to when this one was sent — it may exist past what we could read. Check the profile and resolve it by hand.',
        unknown:
          'LinkedIn could not confirm the post as present or absent. Check the profile and resolve it by hand.',
      },
    },

    schedule: {
      action: 'Schedule',
      timeLabel: 'Time',
      timezoneNote: 'Times in {{timezone}}',
      confirm: 'Schedule post',
      confirmReschedule: 'Reschedule',
      banner: {
        title: 'Scheduled for {{datetime}}',
        editLocked:
          'The text is locked while the post is scheduled — cancel the schedule to edit it.',
        reschedule: 'Reschedule',
        cancel: 'Cancel schedule',
        cancelTitle: 'Cancel this schedule?',
        cancelBody:
          'The post goes back to being a draft and will not be published automatically.',
        cancelConfirm: 'Cancel schedule',
        cancelKeep: 'Keep it scheduled',
      },
      toast: {
        scheduled: 'Post scheduled',
        cancelled: 'Schedule cancelled',
      },
      errors: {
        tooSoon: 'Pick a time at least {{minutes}} minutes from now.',
        scheduleFailed: 'The post could not be scheduled.',
        cancelFailed: 'The schedule could not be cancelled.',
      },
    },

    publishError: {
      genericTitle: 'The post was not published.',

      unconfirmedTitle: 'We could not confirm whether the post went out.',
      unconfirmedBody:
        'LinkedIn never answered, so the post may already be live. Check it before trying again — publishing twice cannot be undone from here.',
      checkOnLinkedIn: 'Check on LinkedIn',
      unconfirmedAcknowledge: 'I checked, it is not there',
      unconfirmedAcknowledged: 'Publishing is enabled again.',

      notRecordedTitle: 'The post IS published, but we could not record it.',
      notRecordedBody:
        'Save this link: it is the only copy we have. Record the publication by hand so the piece closes properly.',
      notRecordedAcknowledge: 'I saved the link',
      copyUrl: 'Copy the link',
      urlCopied: 'Link copied',
      registerManually: 'Record it by hand',
    },

    unsaved: {
      indicator: 'Unsaved changes',
      title: 'You have unsaved changes',
      body:
        'What you wrote is not on the draft yet. If you leave this piece now, the composed text is lost.',
      stay: 'Keep editing',
      leave: 'Leave without saving',
      saveAndLeave: 'Save and leave',
      saving: 'Saving…',
    },

    versions: {
      action: 'Edit history',
      title: 'Edit history',
      description:
        'Every save that changed the text of this post, plus every restore — newest first.',
      empty: 'No versions yet. They appear when a save changes the text.',
      version: 'v{{version}}',
      back: 'Back to the list',
      firstComment: 'First comment',
      trigger: {
        manual_edit: 'Manual edit',
        restore: 'Restore',
      },
      time: {
        justNow: 'Just now',
        minutesAgo: '{{minutes}}m ago',
        hoursAgo: '{{hours}}h ago',
        daysAgo: '{{days}}d ago',
      },
      restore: {
        action: 'Restore this version',
        title: 'Restore v{{version}}?',
        body:
          'The text in the editor is replaced with this version. The current saved text becomes a new version first, so nothing saved is lost.',
        dirtyWarning:
          'You have unsaved changes in the editor — restoring replaces them too, and they cannot be recovered.',
        confirm: 'Restore',
        cancel: 'Cancel',
        blocked:
          'This post can no longer be edited, so it cannot be restored. Cancel the schedule first, or leave a published post as it went out.',
      },
      retry: 'Retry',
      toast: {
        restored: 'Version restored',
      },
      errors: {
        loadFailed: 'Could not load the versions.',
        detailLoadFailed: 'Could not load this version.',
        restoreFailed: 'Could not restore the version.',
      },
    },

    toast: {
      draftSaved: 'Draft saved',
      published: 'Published on LinkedIn',
      publishedWithoutComment: 'Published, but the first comment did not go out',
    },

    errors: {
      loadFailed: 'The draft could not be loaded.',
      saveFailed: 'The draft could not be saved.',
    },
  },

  uploader: {
    dropLabel: 'Drag and drop images here, or click to choose them',
    dropHint: 'Up to {{maxFiles}} images, {{maxSize}} each',
    dragActive: 'Drop the images here',
    compactLabel: 'Add images',
    checking: 'Checking the images…',
    limitReached: 'A post takes {{maxFiles}} images at most.',
    attachedCount: '{{value}} attached',
    dragToReorder: '(drag to reorder)',
    dragToReorderHandle: 'Drag to reorder {{fileName}}',
    removeFile: 'Remove file',

    kind: {
      image: 'images',
      video: 'a video',
      pdf: 'a PDF',
    },

    errors: {
      mixedTypes: 'A post can carry only {{kind}}, so the rest were left out.',
      tooMany: 'A post takes {{max}} {{kind}} at most — the extra ones were left out.',
      unsupportedType: '{{fileName}} is not a supported file type. Use JPG, PNG or GIF.',
      fileTooLarge: '{{fileName}} is larger than {{maxSize}}.',
      imageDimensions: '{{fileName}} is wider or taller than {{maxDimension}} px.',
      imageResolution: '{{fileName}} is over {{maxMegapixels}} megapixels.',
      gifPixels: '{{fileName}} is a GIF over {{maxGifPixels}} pixels.',
    },
  },
}
